import { BRAND } from "constants/brand";
import DropDownButtons from "components/Ui/DropDownButtons/DropDownButtons";
import MenuSimple from "./Dropdown";
import { hasDriverAtRestaurant, hasDriverMark } from "utils/orderStatusStage";
import { NavLink } from "react-router-dom";
import UpdateTime from "./updateTime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
    faMapLocationDot,
    faPrint,
    faShop,
    faUser,
    faBell,
    faCopy,
    faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import Select from "components/Ui/SelectField/SelectField";
import { useRef, useState } from "react";
import TableBodyCells from "components/Ui/TableBodyCells/TableBodyCells";
import UserNote from "components/Ui/UserNote/UserNote";
import { useReactToPrint } from "react-to-print";
import Receipt from "../Receipt";
import { getOrder, notifyVendor } from "services/order";
import { buildOrderClipboardText, copyTextToClipboard } from "util/orderClipboard";
import DeleteItem from "components/Ui/DeleteItem/DeleteItem";
import { formatMoney } from "utils/money";
import OrderWalletCell, { formatSettledNote } from "components/Ui/OrderWalletCell/OrderWalletCell";
import { readApiError } from "utils/apiError";
import {
    formatPickupDelta,
    isPickupOnTime,
    minutesBetween,
    formatDuration,
    formatDateTime,
} from "utils/orderTiming";
import PrepTimer from "components/Ui/PrepTimer/PrepTimer";
import { ltrText } from "utils/dateFormat";

/**
 * One row of the /orders table — the dashboard's densest surface: an operator
 * runs an entire order from here (reassign the driver, move the status, adjust
 * prep time, print, message the customer, re-ping the vendor) without opening
 * the detail page. That is why every cell is an editable control rather than
 * text, and why the row is a `renderers` map: the operator chooses and reorders
 * columns via TableColumnsMenu, so cells must be addressable by id.
 *
 * TYPE ICONS (id cell) — the ONLY thing distinguishing the order entry points,
 * since the list is never filtered by type:
 * - faUser, green  → type="normal": a real customer checkout from the app.
 * - faShop, red    → type="delivery": the vendor asked for a driver; there is
 *   no customer account behind it.
 * - faWhatsapp, orange → the fallback branch, so in practice type="whatsapp":
 *   an order transcribed by an operator OR filed by a dispatch vendor from the
 *   restaurant app. Those two are indistinguishable in this UI even though they
 *   carry different money (a transcribed order has a real goods value, a
 *   quick-dispatch one does not).
 *
 * WHERE THE ID LINKS: normal orders go to the relative `:id` route
 * (/orders/:id, the detail page that renders cart lines); every other type is
 * sent to the absolute /deliveryOrders/:id instead, because those orders have
 * no cart lines and that detail page is the productless variant. Note the two
 * routes are guarded differently — /orders/:id requires `manage_orders` while
 * /deliveryOrders/:id has no permission condition — so this fork can hand a
 * user a page their own list page would have refused them.
 *
 * DRIVER ASSIGNMENT: choosing a driver PATCHes only `deliveryId`, and the API
 * decides the status (api#145) — see `updateDelivery` below. The reset button
 * is the inverse (status back to "finished", driver cleared) and is sent as
 * JSON because FormData cannot express null. Candidates are filtered to drivers in the ORDER's city, not the
 * admin's. Once an order is `complete` with a driver the cell becomes read-only
 * text, so a completed order cannot be reassigned without first moving its
 * status backwards.
 *
 * COLUMN MEANINGS THAT LIE: `lat` is a SECOND PHONE NUMBER, not a latitude —
 * rendered under the name only when it differs from `phone`. The نوع الطلب
 * column reads `lang`, which is the fulfilment mode (توصيل / استلام), not the
 * order type shown by the icon.
 *
 * KNOWN CONSTRAINTS (documented, not fixed):
 * - Cell renderers run inside TableBodyCells' ErrorBoundary, so a row whose
 *   payload is missing an expected association (`vendor.user`, `delivery.user`)
 *   degrades to a single "تعذّر عرض هذا الصف" cell instead of blanking the
 *   page. Anything computed in this component's body ABOVE the return —
 *   `deliveriesOptions`, `selectedDelivery` — is outside that boundary and
 *   would take the whole table down instead.
 * - `updateDelivery(selected?.value)` is reached from react-select's onChange;
 *   a clear event passes null and appends the string "undefined" as deliveryId.
 * - The hook reports a failure by RETURNING the axios error rather than
 *   throwing it, so every write here reads the return value through
 *   `readApiError` and shows the server's own Arabic sentence. It used to
 *   ignore it: a rejected driver change showed nothing at all, and the
 *   optimistic row simply snapped back on the rollback refetch, which looked
 *   like a click that never registered.
 * - The WhatsApp deep link assumes a local 0-prefixed Palestinian number
 *   (`972` + `phone.slice(1)`). The phone guard here is what keeps a
 *   phone-less order from throwing; the /deliveryOrders twin lacks it.
 */
const Order = (props) => {
    const adminInfo = useSelector((state) => state.auth.userData);
    // Internal customer notes are staff-only: vendors never see them (the cell
    // itself is role-gated), and editing needs manage_users on top of reading.
    const canEditNotes = !!(adminInfo?.super_admin || adminInfo?.roles?.manage_users);

    const updateTime = async (time) => {
        const fm = new FormData();
        fm.append("time", time);
        const failure = readApiError(
            await props.editOrder(props.order.id, fm, "normal"),
            "فشل تحديث وقت التحضير",
        );
        if (failure) props.showMessage?.("error", "هناك خطأ", failure);
    };

    const componentRef = useRef(null);
    const [notifying, setNotifying] = useState(false);
    const [copyingOrderData, setCopyingOrderData] = useState(false);
    const [showResetDelivery, setShowResetDelivery] = useState(false);
    const [resettingDelivery, setResettingDelivery] = useState(false);
    const [hoveringReset, setHoveringReset] = useState(false);

    /**
     * Re-pings the vendor about this order. The operational reason it exists:
     * vendors regularly miss the original alert (phone asleep, app killed,
     * receipt printer offline) and the operator needs to nudge them without
     * touching the order's status or money. Guarded against double-taps only by
     * local state — the endpoint itself is not rate limited.
     */
    const handleNotifyVendor = async () => {
        if (notifying) return;
        setNotifying(true);
        try {
            // The API returns 200 with `sent: false` when the vendor has no
            // reachable device — a processed request, not an error, so it never
            // reaches the catch. Reporting it as success is what made a silent
            // failure look like a delivered alert.
            const response = await notifyVendor(props.order.id);
            if (response?.sent) {
                props.showMessage?.("success", "تم الارسال", "تم إرسال الإشعار بنجاح");
            } else {
                props.showMessage?.(
                    "warn",
                    "لم يصل الإشعار",
                    "لا يوجد جهاز مستلم للمطعم — اتصل بالمطعم",
                );
            }
        } catch {
            props.showMessage?.("error", "فشل الارسال", "فشل إرسال الإشعار");
        } finally {
            setNotifying(false);
        }
    };

    /**
     * Fetches full order details by id, then copies a concise vendor-facing Arabic summary.
     *
     * Re-fetches rather than using the row payload because the list response
     * omits the cart lines the summary needs; `props.order` is only the
     * fallback when the response arrives without an `order` key. The operator
     * pastes the result into WhatsApp to a vendor who has no app access.
     */
    const handleCopyOrderData = async () => {
        if (copyingOrderData) return;

        setCopyingOrderData(true);
        try {
            const response = await getOrder(props.order.id);
            const fullOrder = response?.order || props.order;
            const orderDataAsText = buildOrderClipboardText(fullOrder);

            await copyTextToClipboard(orderDataAsText);
            props.showMessage?.("success", "تم النسخ", "تم نسخ بيانات الطلب");
        } catch {
            props.showMessage?.("error", "فشل النسخ", "تعذر نسخ بيانات الطلب");
        } finally {
            setCopyingOrderData(false);
        }
    };

    // Drivers are offered per ORDER city, not per admin city: a super_admin
    // viewing all cities must not be able to hand a Ramallah order to a Jenin
    // driver. `props.users` is already role="delivery" filtered by the page.
    const deliveriesOptions = props.users
        ?.filter((u) => u.cityId === +props.order?.cityId)
        .map((v) => ({
            value: v.id,
            label: v.name,
        }));

    // Pass `null` (not `undefined`) when no match — undefined would put react-select
    // into uncontrolled mode and the previous selection would stay visible after clear.
    const selectedDelivery =
        deliveriesOptions.find((opt) => opt.value === props.order?.deliveryId) || null;

    /**
     * Assigns a driver. SENDS ONLY THE DRIVER, and the API picks the status
     * (api#145). While `DRIVER_ACCEPT_KEEPS_STATUS_ENABLED` is off, the API
     * writes `in the way` for an order still at the restaurant, exactly as
     * this call used to: the driver app on phones finds its own orders only by
     * `in the way` and `picked up`. Once the flag is on, the order keeps its
     * status, the same as a driver's «قبول», because `in the way` means on the
     * way to the customer. Keeping this rule in the API is what lets the flag
     * flip without a dashboard release.
     *
     * @param {number|undefined} deliveryId - The driver's user id. Arrives as
     *   undefined if react-select emits a clear, which serialises to the string
     *   "undefined" through FormData — use the reset button to unassign.
     */
    const updateDelivery = async (deliveryId) => {
        const fm = new FormData();
        fm.append("deliveryId", deliveryId);
        // The refusals that reach here are the ones an operator can act on: the
        // driver's wallet has no balance, the order is already settled, the
        // order is not claimable yet. Each carries its own Arabic sentence.
        const result = await props.editOrder(props.order.id, fm);
        const failure = readApiError(result, "فشل إسناد الطلب إلى السائق");

        if (failure) {
            props.showMessage?.("error", "هناك خطأ", failure);
            return;
        }

        // An admin may assign above a driver's own order ceiling — the ceiling
        // binds the driver claiming from his app, not the dashboard (api#139).
        // The API lets it through and says so; going over it silently is its
        // own problem, so the operator is told what he just did.
        if (result?.exceeded_ceiling) {
            props.showMessage?.("warn", "تجاوز سقف الطلبات", result.warning);
        }
    };

    /**
     * Unassigns the driver and returns the order to "انتهى التحضير", putting it
     * back in the pool for another driver to claim. Exists because a driver who
     * accepts and then cannot deliver would otherwise strand the order in
     * "in the way" with no way back.
     *
     * Confirmed behind a dialog because it re-notifies drivers.
     */
    const confirmResetDelivery = async () => {
        if (resettingDelivery) return;
        setResettingDelivery(true);
        try {
            // Send as JSON (plain object) so `deliveryId: null` is unambiguous
            // on the server — FormData stringifies values, which makes "clear"
            // intent harder to express.
            const failure = readApiError(
                await props.editOrder(props.order.id, {
                    status: "finished",
                    deliveryId: null,
                }),
                "فشل إلغاء إسناد السائق",
            );

            if (failure) {
                props.showMessage?.("error", "هناك خطأ", failure);
                return;
            }

            setShowResetDelivery(false);
        } finally {
            setResettingDelivery(false);
        }
    };

    const handlePrint = useReactToPrint({
        content: () => {
            const printableComponent = componentRef.current;

            if (printableComponent) {
                printableComponent.classList.remove("hidden");
            }
            return printableComponent;
        },
        onAfterPrint: () => {
            const printableComponent = componentRef.current;
            if (printableComponent) {
                printableComponent.classList.add("hidden");
            }
        },
    });

    const cellRenderers = {
        id: () => (
            <td style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {props.order?.type === "normal" ? (
                    <FontAwesomeIcon icon={faUser} size="lg" color="green" />
                ) : props.order?.type === "delivery" ? (
                    <FontAwesomeIcon icon={faShop} size="lg" color="red" />
                ) : (
                    <FontAwesomeIcon icon={faWhatsapp} size="xl" color="#ff9500ff" />
                )}
                <NavLink
                    style={{ color: "blue" }}
                    to={
                        props.order?.type === "normal"
                            ? `${props.order?.id}`
                            : `/deliveryOrders/${props.order?.id}`
                    }
                >
                    {props.order?.id}
                </NavLink>
            </td>
        ),
        name: () => (
            <td className="name">
                <span>
                    <div>
                        <h6 style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span>{props.order?.name}</span>
                            {props.order?.user && adminInfo?.role !== "vendor" ? (
                                <UserNote
                                    userId={props.order.user.id}
                                    note={props.order.user.note}
                                    userName={props.order.user.name || props.order?.name}
                                    editable={canEditNotes}
                                    showMessage={props.showMessage}
                                />
                            ) : null}
                        </h6>
                        <p>{props.order?.phone}</p>
                        {props.order?.lat &&
                        props.order?.lat.trim() !== props.order?.phone?.trim() ? (
                            <p>{props.order?.lat}</p>
                        ) : null}
                    </div>
                </span>
            </td>
        ),
        vendor: () => <td>{props.order?.vendor?.user?.name}</td>,
        total: () => <td>{formatMoney(props.order?.total)}</td>,
        orderRating: () => (
            <td>
                {props.order?.ratingCount > 0 ? (
                    <span style={{ color: "#f5a623", fontWeight: 600, whiteSpace: "nowrap" }}>
                        ★ {Number(props.order.ratingAvg).toFixed(2)}{" "}
                        <span style={{ color: "#777", fontWeight: 400 }}>
                            ({props.order.ratingCount})
                        </span>
                    </span>
                ) : (
                    <span style={{ color: "#aaa" }}>—</span>
                )}
            </td>
        ),
        status: () => (
            <td>
                <MenuSimple
                    id={props.order.id}
                    updateOrder={props.editOrder}
                    status={props.order?.status}
                    withDriver={hasDriverAtRestaurant(props.order)}
                    driverMark={hasDriverMark(props.order)}
                    showMessage={props.showMessage}
                />
            </td>
        ),
        prepTimer: () => (
            <td>
                <PrepTimer order={props.order} />
            </td>
        ),
        prepTime: () => (
            <td>
                <UpdateTime updateTime={updateTime} value={props.order?.time} />
            </td>
        ),
        /**
         * How this order went against the restaurant's own estimate.
         *
         * The number comes from the API (`pickup_delta_seconds`), which computes
         * it from the same definition as the driver's average — a cell that did
         * its own arithmetic could disagree with the score on the driver's row.
         *
         * EMPTY IS NOT ZERO, and it says which kind of empty it is. An order
         * with no pickup stamp reads «لم يُسجَّل»; one the restaurant never gave
         * an estimate for reads «بلا تقدير». Both used to be «—», and so did an
         * order collected exactly on time.
         */
        pickupDelta: () => {
            const label = formatPickupDelta(props.order?.pickup_delta_seconds);

            if (!label) {
                return (
                    <td>
                        <span className="text-muted">
                            {props.order?.picked_up_at
                                ? "لا يوجد وقت تحضير"
                                : props.order?.deliveryId
                                  ? "لم يُسجَّل الاستلام"
                                  : "—"}
                        </span>
                    </td>
                );
            }

            return (
                <td>
                    <span
                        className={`order-timing ${
                            isPickupOnTime(props.order.pickup_delta_seconds)
                                ? "order-timing--early"
                                : "order-timing--late"
                        }`}
                        title={`استلمه السائق من المطعم: ${ltrText(formatDateTime(props.order?.picked_up_at))}`}
                    >
                        {label}
                    </span>
                </td>
            );
        },
        /**
         * How long the delivery leg took: from the moment the order became this
         * driver's to the moment it reached the customer.
         *
         * `assigned_at` rather than `accepted_at`, because the second is null
         * for every order an admin handed out — a quarter of them — and a
         * duration that silently skips those is worse than one that admits it.
         */
        deliveryTime: () => {
            const minutes = minutesBetween(props.order?.assigned_at, props.order?.delivered_at);
            const label = formatDuration(minutes);

            return (
                <td>
                    {label ? (
                        <span
                            className="order-timing"
                            title={`سُلّم للزبون: ${ltrText(formatDateTime(props.order?.delivered_at))}`}
                        >
                            {label}
                        </span>
                    ) : (
                        <span className="text-muted">
                            {props.order?.status === "complete" ? "غير متاح" : "—"}
                        </span>
                    )}
                </td>
            );
        },
        /**
         * The road only: from the pickup at the restaurant to the customer. Next to
         * «من الإسناد حتى التسليم», which also counts the wait at the restaurant, so
         * the two together say where a slow delivery lost its time.
         */
        roadTime: () => {
            const minutes = minutesBetween(props.order?.picked_up_at, props.order?.delivered_at);
            const label = formatDuration(minutes);

            return (
                <td>
                    {label ? (
                        <span
                            className="order-timing"
                            title={`استلمه السائق من المطعم: ${ltrText(formatDateTime(props.order?.picked_up_at))} · سُلّم للزبون: ${ltrText(formatDateTime(props.order?.delivered_at))}`}
                        >
                            {label}
                        </span>
                    ) : (
                        <span className="text-muted">
                            {props.order?.status === "complete" && !props.order?.picked_up_at
                                ? "لم يُسجَّل الاستلام"
                                : "—"}
                        </span>
                    )}
                </td>
            );
        },
        area: () => <td>{props.order?.area?.name || <span className="text-muted">—</span>}</td>,
        /**
         * The delivery charge, which the orders page never showed.
         *
         * It is the basis for the driver commission AND for the wallet fee, so
         * an operator reconciling either could not see the number the money is
         * computed from — only the combined total beside it.
         */
        shipping: () => <td>{formatMoney(props.order?.shipping)}</td>,
        delivery: () => (
            <td>
                {props.order?.status === "complete" && props.order?.deliveryId ? (
                    props.order.delivery?.user?.name
                ) : adminInfo.role !== "vendor" ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <Select
                            options={deliveriesOptions}
                            styles={{
                                container: (base) => ({
                                    ...base,
                                    width: "150px",
                                }),
                            }}
                            placeholder="اختر السائق"
                            value={selectedDelivery}
                            isSearchable
                            onChange={(selected) => {
                                updateDelivery(selected?.value);
                            }}
                        />
                        {props.order?.deliveryId ? (
                            <button
                                type="button"
                                onClick={() => setShowResetDelivery(true)}
                                onMouseEnter={() => setHoveringReset(true)}
                                onMouseLeave={() => setHoveringReset(false)}
                                title="إلغاء تعيين السائق"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    backgroundColor: hoveringReset ? "#dc3545" : "transparent",
                                    color: hoveringReset ? "#fff" : "#dc3545",
                                    border: "1.5px solid #dc3545",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    transition: "background-color 150ms ease, color 150ms ease",
                                }}
                            >
                                <FontAwesomeIcon icon={faRotateLeft} />
                            </button>
                        ) : null}
                    </div>
                ) : (
                    "لا يوجد"
                )}
            </td>
        ),
        type: () => <td>{props.order.lang === "delivery" ? "توصيل" : "استلام"}</td>,
        isFree: () => <td>{props.order.isFree ? "مجاني" : "غير مجاني"}</td>,
        createdAt: () => (
            <td>
                <bdi dir="ltr">{formatDateTime(props.order.createdAt)}</bdi>
            </td>
        ),
        deletedForVendor: () => (
            <td style={{ color: props.order.deleted_for_vendor ? "red" : "green" }}>
                {props.order.deleted_for_vendor ? "نعم" : "لا"}
            </td>
        ),
        deletedForDelivery: () => (
            <td style={{ color: props.order.deleted_for_delivery ? "red" : "green" }}>
                {props.order.deleted_for_delivery ? "نعم" : "لا"}
            </td>
        ),
        // THE TWO WALLETS, the same cell on both orders pages (admin#41): what
        // each wallet holds for this order, «—» when it holds nothing. The
        // driver cell adds when its fee was settled.
        vendorWallet: () => (
            <OrderWalletCell
                amount={props.order.vendor_wallet_commission}
                note={formatSettledNote(props.order.vendor_wallet_settled_at)}
            />
        ),
        driverWallet: () => (
            <OrderWalletCell
                amount={props.order.driver_wallet_fee}
                note={formatSettledNote(props.order.delivery_wallet_settled_at)}
            />
        ),
        actions: () => (
            <td
                style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    paddingTop: "10px",
                }}
            >
                {props.order?.phone && (
                    <a
                        href={`https://wa.me/972${props.order.phone.slice(
                            1,
                        )}?text="مرحبا طلبت طلبية من تطبيق طلباتك"`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FontAwesomeIcon icon={faWhatsapp} size="xl" />
                    </a>
                )}
                <a onClick={handlePrint}>
                    <FontAwesomeIcon icon={faPrint} size="xl" color="blue" />
                </a>
                <a
                    onClick={handleCopyOrderData}
                    style={{
                        cursor: copyingOrderData ? "not-allowed" : "pointer",
                        opacity: copyingOrderData ? 0.5 : 1,
                    }}
                    title="نسخ بيانات الطلب"
                >
                    <FontAwesomeIcon icon={faCopy} size="xl" color={BRAND} />
                </a>
                {props.order?.location && (
                    <a href={props.order?.location} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faMapLocationDot} size="xl" color="red" />
                    </a>
                )}
                {/*
                    Staff only, like the edit/delete actions just below. It pokes
                    the restaurant about a late order, which is an operator
                    action, not something a restaurant does to itself — and the
                    API gates POST /api/order/:id/notify-vendor as admin-only, so
                    rendering it for a vendor only produces a 403.
                */}
                {adminInfo.role !== "vendor" && (
                    <a
                        onClick={handleNotifyVendor}
                        style={{
                            cursor: notifying ? "not-allowed" : "pointer",
                            opacity: notifying ? 0.5 : 1,
                        }}
                        title="إرسال إشعار للمطعم"
                    >
                        <FontAwesomeIcon icon={faBell} size="xl" color="#f59e0b" />
                    </a>
                )}
                <DropDownButtons
                    vendor={props.vendor}
                    edit={
                        adminInfo.role !== "vendor"
                            ? () => {
                                  props.setSelectedOrderValue(props.order);
                                  props.toggleEdit();
                              }
                            : undefined
                    }
                    delete={
                        adminInfo.role !== "vendor"
                            ? () => {
                                  props.setSelectedOrderValue(props.order);
                                  props.toggleDelete();
                              }
                            : undefined
                    }
                    deleteForVendor={
                        adminInfo.role !== "vendor"
                            ? () => {
                                  props.onRequestDeleteForVendor?.(props.order);
                              }
                            : undefined
                    }
                    deleteForVendorDisabled={props.order.deleted_for_vendor}
                ></DropDownButtons>
            </td>
        ),
    };

    return (
        <tr className="item" style={{ maxHeight: "100px" }}>
            <TableBodyCells visibleColumns={props.visibleColumns} renderers={cellRenderers} />
            <Receipt ref={componentRef} order={props.order} />
            <DeleteItem
                show={showResetDelivery}
                close={() => setShowResetDelivery(false)}
                confirm={confirmResetDelivery}
                loading={resettingDelivery}
                icon={faRotateLeft}
                title="إعادة تعيين الطلب"
                message="هل تريد إلغاء تعيين السائق وإعادة الطلب إلى انتهى التحضير؟"
                confirmLabel="إعادة التعيين"
            />
        </tr>
    );
};

export default Order;
